"use client";

import { Box, Button, Checkbox, Group, Stack, Text } from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./ScopeSelection.module.scss";

const REQUIRED_SCOPES = new Set(["openid"]);
const SCOPE_LABELS: Record<string, string> = {
  openid: "Basic identity",
  profile: "Profile details",
  email: "Email address",
  offline_access: "Keep me signed in",
};

/**
 * Renders the scope selector step for login flows.
 */
export function ScopeSelection({
  scope,
  isSubmitting = false,
  onSubmit,
  onBack,
  title = "Select access",
  subtitle = "Choose the permissions you want to grant.",
  errorMessage,
}: ScopeSelectionProps) {
  //------------------------------------------------------------------------------------------------
  //                                         DEPENDENCIES
  //------------------------------------------------------------------------------------------------
  const scopeOptions = useMemo(() => buildScopeOptions({ scope }), [scope]);

  //------------------------------------------------------------------------------------------------
  //                                            STATE
  //------------------------------------------------------------------------------------------------
  const [selectedScopes, setSelectedScopes] = useState<string[]>(() =>
    scopeOptions.map((option) => option.value),
  );

  const isSubmitDisabled = isSubmitting || selectedScopes.length === 0;

  //------------------------------------------------------------------------------------------------
  //                                          FUNCTIONS
  //------------------------------------------------------------------------------------------------
  /**
   * Updates the selected scope list while preserving required scopes.
   */
  const handleSelectionChange = useCallback((values: string[]) => {
    const nextSelections = new Set(values);
    for (const scopeValue of REQUIRED_SCOPES) {
      nextSelections.add(scopeValue);
    }
    setSelectedScopes(Array.from(nextSelections));
  }, []);

  /**
   * Submits the selected scopes back to the caller.
   */
  const handleSubmit = useCallback(() => {
    const resolvedScope = buildScopeString({ selectedScopes, scopeOptions });
    onSubmit({ scope: resolvedScope });
  }, [onSubmit, scopeOptions, selectedScopes]);

  //------------------------------------------------------------------------------------------------
  //                                           EFFECTS
  //------------------------------------------------------------------------------------------------
  useEffect(() => {
    setSelectedScopes(scopeOptions.map((option) => option.value));
  }, [scopeOptions]);

  //------------------------------------------------------------------------------------------------
  //                                           RENDER
  //------------------------------------------------------------------------------------------------
  return (
    <Stack className={styles.Container} gap="xl">
      <Stack gap="xxs">
        <Text className={styles.Title}>{title}</Text>
        <Text className={styles.Subtitle}>{subtitle}</Text>
      </Stack>

      {errorMessage && (
        <Box className={styles.ErrorMessage}>
          <Text size="sm">{errorMessage}</Text>
        </Box>
      )}

      <Checkbox.Group
        value={selectedScopes}
        onChange={handleSelectionChange}
        className={styles.ScopeList}
      >
        <Stack gap="xs">
          {scopeOptions.map((option) => (
            <Checkbox
              key={option.value}
              value={option.value}
              label={option.label}
              description={option.description}
              disabled={option.isRequired}
            />
          ))}
        </Stack>
      </Checkbox.Group>

      <Group justify="space-between" className={styles.Footer}>
        {onBack ? (
          <Button variant="subtle" onClick={onBack} disabled={isSubmitting}>
            Back to profiles
          </Button>
        ) : (
          <span />
        )}
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitDisabled}
        >
          Continue
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * Builds selectable scope metadata from a raw scope string.
 */
function buildScopeOptions({ scope }: { scope: string }): ScopeOption[] {
  const scopeValues = Array.from(
    new Set(
      scope
        .split(" ")
        .map((value) => value.trim())
        .filter((value) => value.length > 0),
    ),
  );

  return scopeValues.map((value) => ({
    value,
    label: SCOPE_LABELS[value] ?? value,
    description: buildScopeDescription({ scopeValue: value }),
    isRequired: REQUIRED_SCOPES.has(value),
  }));
}

/**
 * Builds the final scope string from selections.
 */
function buildScopeString({
  selectedScopes,
  scopeOptions,
}: {
  selectedScopes: string[];
  scopeOptions: ScopeOption[];
}): string {
  const availableScopes = new Set(scopeOptions.map((option) => option.value));
  const filtered = selectedScopes.filter((scopeValue) => availableScopes.has(scopeValue));
  return filtered.join(" ");
}

/**
 * Builds a short description for known scopes.
 */
function buildScopeDescription({ scopeValue }: { scopeValue: string }): string | undefined {
  if (scopeValue === "openid") {
    return "Required to sign in.";
  }
  if (scopeValue === "offline_access") {
    return "Allows longer sessions without re-authentication.";
  }
  return undefined;
}

interface ScopeSelectionProps {
  readonly scope: string;
  readonly isSubmitting?: boolean;
  readonly onSubmit: (params: { scope: string }) => void | Promise<void>;
  readonly onBack?: () => void;
  readonly title?: string;
  readonly subtitle?: string;
  readonly errorMessage?: string;
}

interface ScopeOption {
  readonly value: string;
  readonly label: string;
  readonly description?: string;
  readonly isRequired: boolean;
}
